/**
 * `LoyaltyRewardDto` / `LoyaltyRewardBindingModel`, from
 * `Gopanza.Api.Services.Abstractions`.
 */

/**
 * `LoyaltyRewardType`.
 *
 * The two `item*` types need an `ItemId` — `CreateOrUpdateLoyaltyRewardAsync`
 * refuses them without one — and the two `*Percentage` types read `value` as a
 * fraction rather than a percentage.
 */
export const REWARD_TYPE = {
  fixedValue: 0,
  percentageValue: 1,
  itemFixed: 2,
  itemPercentage: 3,
} as const;

export function isPercentage(type: number): boolean {
  return (
    type === REWARD_TYPE.percentageValue || type === REWARD_TYPE.itemPercentage
  );
}

export function needsItem(type: number): boolean {
  return type === REWARD_TYPE.itemFixed || type === REWARD_TYPE.itemPercentage;
}

export interface RewardItem {
  id: string;
  gtin: string | null;
  brand: string | null;
  name: string | null;
  itemImage: string | null;
  itemThumbnail: string | null;
}

export interface LoyaltyReward {
  id: string;
  internalName: string | null;
  name: string | null;
  ordering: number;
  createdAt: string;
  deletedAt: string | null;
  isActive: boolean;
  pointForRedeem: number;
  type: number;
  itemId: string | null;
  /** Currency on the fixed types, a fraction on the percentage ones. */
  value: number;
  minimumOrderTotal: number;
  /**
   * A hard ceiling in currency on whatever the reward works out to.
   *
   * Applied to **every** type, not just the percentages, and applied
   * unconditionally: `if (Discount > MaxValue) Discount = MaxValue`. A reward
   * saved with 0 here therefore discounts nothing at all, whatever else it
   * says — which is what the original produced by default, since it seeded a
   * new reward as `{}` and never filled the field in.
   */
  maxValue: number;
  startDate: string;
  endDate: string;
  item: RewardItem | null;
}

/** Exactly what `POST loyalty/rewards` reads. */
export interface LoyaltyRewardInput {
  id: string | null;
  internalName: string;
  name: string;
  isActive: boolean;
  pointForRedeem: number;
  ordering: number;
  type: number;
  itemId: string | null;
  value: number;
  minimumOrderTotal: number;
  maxValue: number;
  startDate: string | null;
  endDate: string | null;
}

export type RewardStatus = 'live' | 'scheduled' | 'ended' | 'inactive';

export function statusOf(reward: LoyaltyReward, now = Date.now()): RewardStatus {
  if (!reward.isActive) return 'inactive';
  const start = Date.parse(`${reward.startDate}Z`);
  const end = Date.parse(`${reward.endDate}Z`);
  if (!Number.isNaN(end) && now > end) return 'ended';
  if (!Number.isNaN(start) && now < start) return 'scheduled';
  return 'live';
}

export function toInput(reward: LoyaltyReward): LoyaltyRewardInput {
  return {
    id: reward.id,
    internalName: reward.internalName ?? '',
    name: reward.name ?? '',
    isActive: reward.isActive,
    pointForRedeem: reward.pointForRedeem,
    ordering: reward.ordering,
    type: reward.type,
    itemId: reward.itemId,
    value: reward.value,
    minimumOrderTotal: reward.minimumOrderTotal,
    maxValue: reward.maxValue,
    startDate: reward.startDate,
    endDate: reward.endDate,
  };
}

export function emptyReward(): LoyaltyRewardInput {
  return {
    id: null,
    internalName: '',
    name: '',
    isActive: false,
    pointForRedeem: 0,
    ordering: 0,
    type: REWARD_TYPE.fixedValue,
    itemId: null,
    value: 0,
    minimumOrderTotal: 0,
    // Not zero. Zero is a valid number the API accepts and then uses to cap
    // every redemption to nothing; the editor requires a real ceiling.
    maxValue: 0,
    startDate: null,
    endDate: null,
  };
}
